import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  createClinicUser,
  deleteClinicUser,
  fetchClinicUsers,
  updateClinicUserAccess,
} from "@services/clinicUsers";
import {
  extractUsers,
  normalizeRole,
  isValidEmail,
  isValidPassword,
} from "@utils/userFormatters";
import { sanitizeFormValues } from "@utils/sanitize";

import ToastHost from "@components/clinic/ToastHost";
import HeroHeader from "@components/clinic/HeroHeader";
import AddUserCard from "@components/clinic/AddUserCard";
import FiltersBar from "@components/clinic/FiltersBar";
import UsersTable from "@components/clinic/UsersTable";
import ConfirmRemoveModal from "@components/clinic/ConfirmRemoveModal";
import UserAccessDrawer from "@components/clinic/UserAccessDrawer";

export default function UsersPermissionsPage() {
  const isOwner = useMemo(() => {
    const v = localStorage.getItem("is_owner");
    return v === "true" || v === true;
  }, []);

  const [state, setState] = useState({
    users: [],
    loading: false,
    error: null,
  });

  const [createForm, setCreateForm] = useState({
    email: "",
    password: "",
    confirm_password: "",
    role: "STAFF",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [accessDrawer, setAccessDrawer] = useState({ open: false, user: null });
  const [savingAccess, setSavingAccess] = useState(false);

  const [removeModal, setRemoveModal] = useState({ open: false, user: null });
  const [removingId, setRemovingId] = useState(null);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  const navigate = useNavigate();

  const handleBack = () => {
    // go back if possible, otherwise fallback to a safe route
    if (window.history.length > 1) navigate(-1);
    else navigate("/"); // or "/dashboard"
  };

  const loadUsers = async () => {
    setState((p) => ({ ...p, loading: true, error: null }));
    try {
      const res = await fetchClinicUsers();
      const users = extractUsers(res);
      setState({ users, loading: false, error: null });
    } catch (e) {
      setState((p) => ({
        ...p,
        loading: false,
        error: e?.message || "Failed to load users",
      }));
    }
  };

  useEffect(() => {
    if (isOwner) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const createError = useMemo(() => {
    const email = (createForm.email || "").trim();
    if (!email) return "Email is required";
    if (!isValidEmail(email)) return "Enter a valid email";
    if (!createForm.role) return "Role is required";
    if (!createForm.password) return "Password is required";
    if (!isValidPassword(createForm.password)) {
      return "Password must be 8+ chars and include uppercase, lowercase, number, and symbol";
    }
    if ((createForm.password || "").length < 8)
      return "Password must be at least 8 characters";
    if (!createForm.confirm_password) return "Confirm password is required";
    if (createForm.password !== createForm.confirm_password)
      return "Passwords do not match";
    return null;
  }, [createForm]);

  const onCreate = async () => {
    if (createError) {
      setToast({
        kind: "error",
        title: "Cannot add user",
        message: createError,
      });
      return;
    }
    setSaving(true);
    try {
      await createClinicUser(sanitizeFormValues({
        email: createForm.email.trim(),
        password: createForm.password,
        confirm_password: createForm.confirm_password,
        role: createForm.role,
        permission_preset: "FRONTDESK",
      }));

      setCreateForm({
        email: "",
        password: "",
        confirm_password: "",
        role: "STAFF",
      });
      setToast({
        kind: "success",
        title: "User added",
        message: "Clinic user created successfully.",
      });
      await loadUsers();
    } catch (e) {
      setToast({
        kind: "error",
        title: "Failed to add user",
        message: e?.message || "Request failed",
      });
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const users = state.users || [];
    const query = (q || "").trim().toLowerCase();

    return users.filter((u) => {
      const email = (u.email || "").toLowerCase();
      const matchesQuery = !query || email.includes(query);

      const r = normalizeRole(u.role);
      const matchesRole =
        roleFilter === "ALL"
          ? true
          : roleFilter === "NONE"
          ? !r
          : r === roleFilter;

      return matchesQuery && matchesRole;
    });
  }, [state.users, q, roleFilter]);

  const openAccess = (user) => {
    if (user?.is_owner) {
      setToast({
        kind: "error",
        title: "Not allowed",
        message: "Owner access cannot be modified.",
      });
      return;
    }
    setAccessDrawer({ open: true, user });
  };

  const closeAccess = () => setAccessDrawer({ open: false, user: null });

  const onSaveAccess = async ({ role }) => {
    const user = accessDrawer.user;
    if (!user?.id) return;

    const patch = {};

    const currentRole = user.role ?? null;
    if (role != null && role !== currentRole) {
      patch.role = role;
    }

    // Always ensure permission_preset is FRONTDESK
    if (user.permission_preset !== "FRONTDESK") {
      patch.permission_preset = "FRONTDESK";
    }

    if (Object.keys(patch).length === 0) {
      setToast({
        kind: "success",
        title: "No changes",
        message: "Nothing to update.",
      });
      closeAccess();
      return;
    }

    setSavingAccess(true);
    setUpdatingUserId(user.id);

    try {
      await updateClinicUserAccess(user.id, patch);

      setToast({
        kind: "success",
        title: "Access updated",
        message: "User access saved successfully.",
      });

      closeAccess();
      await loadUsers();
    } catch (e) {
      setToast({
        kind: "error",
        title: "Failed to update access",
        message: e?.message || "Request failed",
      });
    } finally {
      setSavingAccess(false);
      setUpdatingUserId(null);
    }
  };

  const openRemove = (user) => {
    if (user?.is_owner) {
      setToast({
        kind: "error",
        title: "Not allowed",
        message: "You cannot delete the owner account.",
      });
      return;
    }
    setRemoveModal({ open: true, user });
  };

  const closeRemove = () => setRemoveModal({ open: false, user: null });

  const onConfirmRemove = async () => {
    const user = removeModal.user;
    if (!user?.id) return;

    setRemovingId(user.id);
    try {
      await deleteClinicUser(user.id);
      setToast({
        kind: "success",
        title: "User removed",
        message: `${user.email} no longer has access.`,
      });
      closeRemove();
      await loadUsers();
    } catch (e) {
      setToast({
        kind: "error",
        title: "Failed to remove user",
        message: e?.message || "Request failed",
      });
    } finally {
      setRemovingId(null);
    }
  };

  if (!isOwner) {
    return (
      <div className="p-6">
        <div className="p-5 border rounded-2xl bg-white">
          <div className="font-semibold text-slate-900">Access denied</div>
          <div className="text-sm text-slate-600">
            Only the clinic owner can manage users.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col min-h-0 h-full gap-4">
      <div className="flex items-center">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white
               hover:bg-slate-50 shadow-sm text-sm font-medium"
        >
          ← Back
        </button>
      </div>
      <ToastHost toast={toast} onClose={() => setToast(null)} />
      <HeroHeader loading={state.loading} />

      <AddUserCard
        form={createForm}
        setForm={setCreateForm}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        saving={saving}
        error={createError}
        onCreate={onCreate}
      />

      <FiltersBar
        q={q}
        setQ={setQ}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
      />

      <UsersTable
        users={filteredUsers}
        total={state.users.length}
        loading={state.loading}
        error={state.error}
        onRemoveClick={openRemove}
        removingId={removingId}
        updatingUserId={updatingUserId}
        onEditAccess={openAccess}
      />

      <UserAccessDrawer
        open={accessDrawer.open}
        user={accessDrawer.user}
        saving={savingAccess}
        onClose={closeAccess}
        onSave={onSaveAccess}
      />

      <ConfirmRemoveModal
        open={removeModal.open}
        user={removeModal.user}
        removing={!!removingId}
        onClose={closeRemove}
        onConfirm={onConfirmRemove}
      />
    </div>
  );
}
